import { useMemo, useState, type FormEvent } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useApp } from '../AppContext'
import { useAsync } from '../hooks/useAsync'
import { hasCapability } from '../auth/roles'
import { Modal } from '../components/Modal'
import { TokenRevealModal } from '../components/TokenRevealModal'
import { PageHeader } from '../components/primitives/PageHeader'
import { Panel } from '../components/primitives/Panel'
import { DataTable, type DataTableColumn } from '../components/primitives/DataTable'
import { Mono } from '../components/primitives/Fact'
import { EnvironmentStatusChip } from '../components/primitives/StatusChip'
import { Field } from '../components/primitives/Field'
import { CheckboxGroup, type CheckboxOption } from '../components/primitives/CheckboxGroup'
import { Notice } from '../components/primitives/Notice'
import { PendingOverlay } from '../components/PendingOverlay'
import { dateInputToIso, expiryState } from '../api/expiry'
import { verifyExpiry, isReadBackProblem } from '../api/readBack'
import { expiryTone, toneVisual } from '../theme/statusTone'
import { refusalSentence } from '../theme/vocabulary'
import { ApiError } from '../api/client'
import {
  SUPPORTED_ENTITLEMENTS,
  type AccountListResponse,
  type Entitlement,
  type EnvironmentListItem,
  type EnvironmentListResponse,
  type TokenIssuanceResponse,
} from '../api/types'

const PAGE_SIZE = 50

// The CRM landing view: a dense, filterable, keyset-paginated table of every
// customer environment, backed by the server-side registry list (ATC-051).
//
// ATC-110 converted this to the light shell. Two things about that:
//
//  • THE TABLE NOW SITS IN A `Panel`, AND THAT IS A CONFORMANCE REQUIREMENT
//    RATHER THAN A COSMETIC ONE. The status hexes fall to 2.98:1 (success) and
//    2.88:1 (warning) on the warm canvas — under even the 3:1 graphical floor
//    — so a status dot may only sit on white. This table sat bare on the page
//    background before, which is why the palette flip could not ship without
//    the primitives.
//  • KEYSET PAGINATION IS PRESERVED. The continuation-token stack below is
//    untouched; only the buttons' glyphs changed. `DataTable` deliberately owns
//    no paging model precisely so it could not push this toward offsets.
export function EnvironmentsList() {
  const { api, role } = useApp()
  const canWrite = hasCapability(role, 'write')
  // ATC-132: `search` and `status` live in the URL. ATC-133's failure recovery
  // links to a filtered list, and it makes any filtered view shareable — an
  // operator can paste "the three suspended ones" into a ticket.
  const [params, setParams] = useSearchParams()

  // ATC-142. A one-line receipt from a delete on the detail page. It is carried
  // in router state rather than refetched because THERE IS NOTHING LEFT TO
  // REFETCH -- the environment is gone, and this is the last moment anything in
  // the console knows its label. Landing on a list and inferring success from an
  // absence is the alternative, and it is indistinguishable from a filter that
  // happened to exclude the row.
  const location = useLocation()
  const deleted = (location.state as { deleted?: { environmentLabel: string; credentialsDeleted: number } } | null)
    ?.deleted
  const status = params.get('status') ?? ''
  const search = params.get('search') ?? ''
  const [searchInput, setSearchInput] = useState(search)
  const [now] = useState(() => Date.now())
  // Keyset pagination: a stack of continuation tokens (one per visited page).
  const [tokenStack, setTokenStack] = useState<(string | null)[]>([null])

  // Issue-environment action state.
  const [showIssue, setShowIssue] = useState(false)
  const [issueEntitlements, setIssueEntitlements] = useState<string[]>(['bootstrap', 'mint'])
  const [issueExpiry, setIssueExpiry] = useState('')
  const [issuing, setIssuing] = useState(false)
  const [issueError, setIssueError] = useState<string | null>(null)
  // ATC-140. ONE call for the page, joined in memory — never one per row, which
  // is what the paginated operator endpoints exist to prevent. Denormalizing
  // `accountName` into `environmentRegistry` would also work and was rejected:
  // it creates a drift vector that `PATCH /v1/accounts` would then have to fan
  // out to every environment row, which is a lot of machinery for a label.
  //
  // It matters BECAUSE of the generated label: a row reading `fizzy-thunder`
  // beside a bare account GUID is less scannable than the typed name it
  // replaced. The label is only an improvement when the customer is legible
  // next to it.
  const accounts = useAsync<AccountListResponse>(() => api.listAccounts({ pageSize: 200 }), [])
  const accountNames = useMemo(() => {
    const map = new Map<string, string>()
    for (const a of accounts.data?.accounts ?? []) {
      if (a.accountName) map.set(a.accountId, a.accountName)
    }
    return map
  }, [accounts.data])

  const [revealed, setRevealed] = useState<TokenIssuanceResponse | null>(null)
  const [revealWarning, setRevealWarning] = useState<string | undefined>(undefined)

  const currentToken = tokenStack[tokenStack.length - 1] ?? undefined

  const { data, loading, error, reload } = useAsync<EnvironmentListResponse>(
    () =>
      api.listEnvironments({
        status: status || undefined,
        search: search || undefined,
        pageSize: PAGE_SIZE,
        continuationToken: currentToken,
      }),
    [status, search, currentToken],
  )

  function resetPaging() {
    setTokenStack([null])
  }

  function setFilter(key: 'search' | 'status', value: string) {
    const next = new URLSearchParams(params)
    if (value) {
      next.set(key, value)
    } else {
      next.delete(key)
    }
    setParams(next, { replace: true })
    resetPaging()
  }

  async function submitIssue(e: FormEvent) {
    e.preventDefault()
    setIssuing(true)
    setIssueError(null)
    try {
      // A console-issued environment used to get `bootstrap,mint` and nothing
      // else — it could not fetch a readiness ruleset and could not build a
      // .ppkg. Flight Deck staging's `readiness` entitlement was granted by a
      // DIRECT TABLE WRITE, the unaudited path ATC-074 exists to eliminate.
      const expiresAt = issueExpiry ? (dateInputToIso(issueExpiry) ?? undefined) : undefined
      const result = await api.issueEnvironment({
        entitledServices: issueEntitlements as Entitlement[],
        expiresAt,
      })
      setShowIssue(false)
      setIssueEntitlements(['bootstrap', 'mint'])
      setIssueExpiry('')
      // ATC-139. Compare what was ASKED FOR against what the server reports
      // storing. Hotfix 054 accepted an expiry, returned 200, and stored
      // nothing — a 200 is exactly what that bug produced, so the status code
      // alone can never be the confirmation.
      const readBack = verifyExpiry(expiresAt, result.expiresAt)
      setRevealWarning(isReadBackProblem(readBack) ? readBack.message : undefined)
      setRevealed(result)
      resetPaging()
      reload()
    } catch (err) {
      setIssueError(
        err instanceof ApiError
          ? refusalSentence(err.code, err.message)
          : err instanceof Error
            ? err.message
            : String(err),
      )
    } finally {
      setIssuing(false)
    }
  }

  // `ppkg` is DISABLED here with the reason stated: it is grantable on
  // flightdeck-class rows only (ATC-ADR-016), and this modal mints the DEVICE
  // credential. Hiding it would make the rule invisible.
  const issueOptions: CheckboxOption[] = SUPPORTED_ENTITLEMENTS.map((value) =>
    value === 'ppkg'
      ? {
          value,
          label: value,
          disabled: true as const,
          hint: 'Flight Deck credentials only — issue one from the environment page after this.',
        }
      : { value, label: value },
  )

  const columns: DataTableColumn<EnvironmentListItem>[] = [
    // ATC-141. ACCOUNT FIRST, and the second column is called ENVIRONMENT.
    //
    // The column used to be headed "Customer" and hold `customerName`, which
    // since ATC-140 is a generated ENVIRONMENT label — so the table named the
    // wrong thing twice over: the customer sat in a column called Account, and
    // the environment sat in one called Customer. An operator reads this page
    // customer-first, which is also the order the data now supports.
    {
      key: 'account',
      header: 'Customer',
      // "NO VALUE" IS NOT "MISSING DATA" (§1.3). This was an em-dash, which
      // reads as a data problem; the real absent state is that the environment
      // is not mapped to a migration-license account. Matches the wording on
      // the environment detail view.
      render: (env) =>
        env.accountId ? (
          // ATC-130: links to the customer. The <=760px breakpoint hides the
          // nav entirely, so Customers must be reachable without it.
          //
          // ATC-140: the NAME when the join has it, the id when it does not —
          // a GUID is still a correct answer, just a worse one, so a failed or
          // truncated accounts call degrades rather than blanking the cell.
          <Link to={`/customers/${env.accountId}`}>
            {accountNames.get(env.accountId) ?? <Mono>{env.accountId}</Mono>}
          </Link>
        ) : (
          <span className="muted">Unmapped</span>
        ),
    },
    {
      key: 'environment',
      header: 'Environment',
      // THE LABEL ONLY. The GUID used to render underneath every row; with a
      // generated label above it the pair was two identifiers where one would
      // do, and the id is a click away as the detail page's subtitle — where
      // it is already mono, tabular and selectable for pasting into a ticket.
      render: (env) => (
        <Link to={`/environments/${env.customerEnvironmentId}`}>
          {env.customerName || <em>(unnamed)</em>}
        </Link>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (env) => <EnvironmentStatusChip status={env.status} />,
    },
    {
      key: 'entitlements',
      header: 'Entitlements',
      // Hotfix 055. The cell shows the DEVICE credential's entitlements plus,
      // separately, whether a Flight Deck credential exists. They are not one
      // list: `ppkg` is grantable on flightdeck-class rows only, so it can
      // never appear among the device entitlements, and an operator reading
      // this column had no way to tell the second credential existed.
      //
      // Rendered as a distinct marker rather than appended to the list,
      // because concatenating them would say something false — that one
      // credential is entitled to all of it, which is precisely the confusion
      // ATC-123 fixed on the detail view.
      render: (env) => (
        <>
          {env.entitledServices.join(', ')}
          {env.hasFlightdeckCredential && (
            <div className="muted">+ Flight Deck (ppkg)</div>
          )}
        </>
      ),
    },
    {
      key: 'issued',
      header: 'Issued',
      render: (env) => <Mono>{formatDate(env.issuedAt)}</Mono>,
    },
    {
      key: 'expires',
      header: 'Expires',
      // "never" is PRESERVED as the absent state in words — a perpetual licence
      // genuinely never expires, and an em-dash here would read as missing data.
      //
      // The chip is a SECOND signal beside the date, not a replacement for the
      // status chip two columns left. An environment can be `active` AND
      // expired: Get-Entitlements refuses it with `token_expired` while this
      // list still reports a green `active`.
      render: (env) => {
        if (!env.expiresAt) {
          return <span className="muted">never</span>
        }
        const state = expiryState(env.expiresAt, now)
        const tone = expiryTone(state)
        return (
          <>
            <Mono>{formatDate(env.expiresAt)}</Mono>
            {tone && (
              <span
                className="rx-chip"
                style={
                  {
                    '--rx-chip-tint': toneVisual(tone).tint,
                    '--rx-chip-dot': toneVisual(tone).hex,
                  } as React.CSSProperties
                }
              >
                <span className="rx-chip-dot" aria-hidden="true" />
                {state === 'expired' ? 'Expired' : 'Expiring'}
              </span>
            )}
          </>
        )
      },
    },
  ]

  return (
    <section>
      <PageHeader
        title="Customer environments"
        actions={
          canWrite && (
            <button className="btn-primary" onClick={() => setShowIssue(true)}>
              Issue environment
            </button>
          )
        }
      />

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search by name (prefix)…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onBlur={() => setFilter('search', searchInput.trim())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              setFilter('search', searchInput.trim())
            }
          }}
        />
        <select value={status} onChange={(e) => setFilter('status', e.target.value)}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="revoked">Revoked</option>
        </select>
      </div>

      {deleted && (
        <Notice tone="ok">
          Deleted <strong>{deleted.environmentLabel || 'the environment'}</strong> and{' '}
          {deleted.credentialsDeleted} credential{deleted.credentialsDeleted === 1 ? '' : 's'}.
          This cannot be undone; the audit trail is in Activity.
        </Notice>
      )}

      {error && <Notice tone="error">{error}</Notice>}

      <Panel flush>
        {/* The error's detail is in the notice above. The table body gets a
            short form of it so it never says "no results" for a request that
            did not complete. */}
        <DataTable
          caption="Customer environments"
          columns={columns}
          data={data?.environments ?? []}
          keyForRow={(env) => env.customerEnvironmentId}
          isLoading={loading}
          error={error ? 'Could not load environments.' : undefined}
          emptyState="No environments match."
        />
      </Panel>

      {data && (
        <div className="pager">
          <button
            disabled={tokenStack.length <= 1}
            onClick={() => setTokenStack((s) => s.slice(0, -1))}
          >
            {/* Decorative — the word carries the direction. */}
            <ChevronLeft size={16} strokeWidth={1.5} aria-hidden="true" />
            Prev
          </button>
          <button
            disabled={!data.continuationToken}
            onClick={() => setTokenStack((s) => [...s, data.continuationToken])}
          >
            Next
            <ChevronRight size={16} strokeWidth={1.5} aria-hidden="true" />
          </button>
        </div>
      )}

      {showIssue && (
        <Modal
          title="Issue customer environment"
          onClose={() => setShowIssue(false)}
          /* Hotfix 053: `deliberate` while a write is in flight. Issuance mints
             a GUID and reveals a token exactly once, so a backdrop click at
             second nine of a thirteen-second cold start must not dismiss the
             dialog the token is about to appear in. */
          dismissal={issuing ? 'deliberate' : undefined}
        >
          {issuing && <PendingOverlay label="Issuing environment…" />}
          <form onSubmit={submitIssue}>
            {/* ATC-140. NO NAME FIELD. The label is server-minted, because its
                only job is DISTINGUISHABILITY — in the list, in `search=`, and
                above all in the destructive-confirm dialogs, which state their
                target by name rather than asking "are you sure". A typed name
                can lie while looking authoritative, nothing enforced its
                uniqueness, and since R-ATC-10 the customer IS the account, so
                it mostly restated one. Naming lives on the customer. */}
            <Notice tone="info">
              A memorable label is generated for you — {'"'}fizzy-thunder{'"'} rather than a GUID. The
              customer this belongs to is set by mapping it on the environment page.
            </Notice>

            <CheckboxGroup
              legend="Entitled services"
              options={issueOptions}
              value={issueEntitlements}
              onChange={setIssueEntitlements}
              disabled={issuing}
            />

            <Field
              label="Expires on"
              hint="Optional. End of the chosen day, UTC — leave blank for a perpetual licence."
            >
              <input
                type="date"
                value={issueExpiry}
                onChange={(e) => setIssueExpiry(e.target.value)}
                disabled={issuing}
              />
            </Field>

            {issueEntitlements.length === 0 && (
              <Notice tone="warn">An environment entitled to nothing cannot be issued.</Notice>
            )}
            {issueError && <Notice tone="error">{issueError}</Notice>}
            <div className="modal-actions">
              <button type="button" onClick={() => setShowIssue(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={issuing || issueEntitlements.length === 0}>
                {issuing ? 'Issuing…' : 'Issue'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {revealed && (
        <TokenRevealModal
          result={revealed}
          variant="issued"
          readBackWarning={revealWarning}
          onClose={() => {
            setRevealed(null)
            setRevealWarning(undefined)
          }}
        />
      )}
    </section>
  )
}

function formatDate(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 10)
}
